import { useToken } from "@gluestack-style/react";
import { Box, Button, FormattedDate, H1, H2, HStack, Link, Text, VStack, Image, Typography } from "components/ui";
import { Col, Row } from "react-native-flex-grid";
import React from "react";
import { EquipmentRead } from "store/abcoApi";
import { useIsMobile } from "utils/hooks/useIsMobile";
import { Boxes, FileText, MapPin, Pen, Tractor, Wrench } from "lucide-react-native";
import EquipmentTracking from "./EquipmentTracking";
import { ServiceOrderScreen } from "screens/ServiceOrderScreen";
import Section from "components/general/Section";
import CardCarousel from "components/general/CardCarousel";
import EquipmentVideoList from "./EquipmentVideoList";
import ItemDownload from "components/item/ItemDownload";
import { Tab, TabContainer } from "components/general/Tabs";
import Seo from "components/general/Seo";

interface Props {
	data: EquipmentRead;
}

export function EquipmentDetail({ data: equipment }: Props) {
	const isMobile = useIsMobile();
	const resolvedPrimarylight = useToken("colors", "primarylight");
	const resolvedNeutral = useToken("colors", "neutral100");

	const labelHeader = { fontFamily: "inter", fontWeight: "bold" };
	const labelText = { fontSize: 16, fontFamily: "inter" };
	const rowStyle = { marginTop: 6, marginBottom: 6, padding: 2 };
	const columnStyle = { maxWidth: 200 };
	const boxWrapper = { marginTop: 10, marginBottom: 10, paddingVertical: 10, paddingHorizontal: 10, bg: "$neutral25" };
	let fileCollection = [];
	var isOverdueForService = false;
	if (equipment && equipment.warrantyEndDate) {
		const today = new Date();
		const nextServiceDueDate = new Date(equipment.nextServiceDueDate ?? "");
		isOverdueForService = today > nextServiceDueDate;
	}

	var lastService = "This machine has not been serviced";
	if (equipment && equipment.lastServiceDate) {
		lastService = `${new Date(equipment.lastServiceDate).toLocaleDateString()}`;
	}

	if (equipment && equipment.fileCollection) {
		fileCollection = JSON.parse(equipment.fileCollection);
	}
	if (!equipment) return null;

	const isPublicView = equipment.id === 0;

	let imageUrlTemp = "/image/aHR0cHM6Ly9zeXN0ZW0ubmV0c3VpdGUuY29tL2NvcmUvbWVkaWEvbWVkaWEubmw_aWQ9OTQ3NyZjPTY3NjM1MjQmaD01bW4wdFpBSWpUN1pJczNPRFRTZmlqcXRCWnd3Q0xZaE5OVXJ4T1RBMHB1M01wazM.dS6JhStZew4.jpg";
	return (
		<Box gap={48}>
			<Section gap={24} padding={65} bg="$basicwhite">
				{/* <Box ml={"auto"}>
					<Button alignSelf="flex-end" gap={8} variant="outline" borderRadius={12}>
						<Pen size={24} color={resolvedPrimarylight} />
						<Typography color="$primarylight">Edit</Typography>
					</Button>
				</Box> */}
				<Box flexDirection={isMobile ? "column" : "row"} width="$full" marginLeft="auto" gap={24} marginRight="auto">
					<Box flex={1} gap={24}>
						<Box maxWidth={600} flex={1} flexDirection="column" paddingBottom={24}>
							{!isPublicView && (
								<Row style={rowStyle}>
									<Col style={columnStyle}>
										<Text sx={labelHeader}>Group / Site</Text>
									</Col>
									<Col>
										<HStack gap={8}>
											{equipment.siteName ? (
												<>
													<MapPin size={24} color={resolvedPrimarylight} />
													<Text sx={{ fontFamily: "inter" }} fontSize={16}>
														{equipment.siteName}
													</Text>
												</>
											) : (
												<Text>Not available</Text>
											)}
										</HStack>
									</Col>
								</Row>
							)}
							<Row style={rowStyle}>
								<Col style={columnStyle}>
									<Text sx={labelHeader}>Type</Text>
								</Col>
								<Col>
									<Text sx={labelText}>{equipment.type}</Text>
								</Col>
							</Row>
							<Row style={rowStyle}>
								<Col style={columnStyle}>
									<Text sx={labelHeader}>Make</Text>
								</Col>
								<Col>
									<Text sx={labelText}>{equipment.make}</Text>
								</Col>
							</Row>
							<Row style={rowStyle}>
								<Col style={columnStyle}>
									<Text sx={labelHeader}>Model</Text>
								</Col>
								<Col>
									<Text sx={labelText}>{equipment.model}</Text>
								</Col>
							</Row>
							<Row style={rowStyle}>
								<Col style={columnStyle}>
									<Text sx={labelHeader}>Serial No</Text>
								</Col>
								<Col>
									<Text sx={labelText}>{equipment.serialNumber}</Text>
								</Col>
							</Row>
							{!isPublicView && equipment.soldDate && (
								<Row style={rowStyle}>
									<Col style={columnStyle}>
										<Text sx={labelHeader}>Purchased</Text>
									</Col>
									<Col>
										<FormattedDate variant="DD/MM/YYYY" date={equipment.soldDate} />
									</Col>
								</Row>
							)}

							{!isPublicView && equipment.warrantyEndDate && (
								<Row style={rowStyle}>
									<Col style={columnStyle}>
										<Text sx={labelHeader}>Purchased</Text>
									</Col>
									<Col>
										<FormattedDate variant="DD/MM/YYYY" date={equipment.warrantyEndDate} />
									</Col>
								</Row>
							)}
							{!isPublicView && (
								<Box borderTopWidth={1} borderColor="$neutral100" paddingTop={16} marginTop={16}>
									<Box mb={16}>
										<Typography weight="bold">Service status</Typography>
									</Box>
									<Section padding={20}>
										<HStack alignItems="center" gap={6}>
											{isOverdueForService && (
												<Typography weight="bold" sx={{ color: "$errorbright" }}>
													Overdue for service:
												</Typography>
											)}
											<Typography weight="bold">Last service:</Typography>
											<Typography>{lastService}</Typography>
										</HStack>
										{/* <Link
									href="/"
									component={(hovered) => {
										return <Text color={hovered ? "$primary" : "$primarylight"}>Confirm availability for service</Text>;
									}}
								></Link> */}
									</Section>
								</Box>
							)}
							{/* <Row style={rowStyle}>
								<Col style={columnStyle}>
									<Text sx={labelHeader}>Maintenance Program</Text>
								</Col>
								<Col>
									<VStack gap={8}>
										<Text sx={labelText}>{equipment.warrantyPeriod ?? "No Plan"}</Text>
										{!equipment.warrantyPeriod && (
											<Link href="#">
												<Text sx={{ color: resolvedPrimarylight, fontWeight: "bold", textDecorationLine: "underline" }}>Add premium maintenance to protect your asset</Text>
											</Link>
										)}
										<Link
											href="/"
											component={(hovered) => {
												return <Text color={hovered ? "$primary" : "$primarylight"}>Add premium maintenance to protect your asset</Text>;
											}}
										></Link>
									</VStack>
								</Col>
							</Row> */}
						</Box>
					</Box>
					<Box aspectRatio={1.32 / 1} flex={1} alignItems="center" borderWidth={1} borderColor="$neutral100" justifyContent="center" borderRadius={16}>
						<Box width={"$full"} justifyContent="center" alignItems="center" padding={48}>
							{/* <CardCarousel shownSlideNumber={1}>
								<Box justifyContent="center" alignItems="center">
									<Image aspectRatio={1.32 / 1} resizeMode="contain" borderRadius={20} width={600} source={process.env.EXPO_PUBLIC_ABCO_API_CDNURL + imageUrlTemp + "?width=400&quality=75&format=webp"} alt={""} />
								</Box>
 
								<Box minHeight={280} alignItems="center" justifyContent="center">
									<Typography.H3>No available image</Typography.H3>
								</Box>
								<Box minHeight={280} alignItems="center" justifyContent="center">
									<Typography.H3>No available image</Typography.H3>
								</Box>
							</CardCarousel> */}
							{equipment.imageUrl && (
								<Image aspectRatio={1.32 / 1} resizeMode="contain" borderRadius={20} width={600} source={process.env.EXPO_PUBLIC_ABCO_API_CDNURL + equipment.imageUrl + "?width=400&quality=75&format=webp"} alt={""} />
							)}
							{/* {imageUrlTemp && <Image aspectRatio={1.32 / 1} resizeMode="contain" borderRadius={20} width={600} source={process.env.EXPO_PUBLIC_ABCO_API_CDNURL + imageUrlTemp + "?width=400&quality=75&format=webp"} alt={""} />} */}
						</Box>
					</Box>
				</Box>
			</Section>
			{fileCollection.length > 0 && (
				<Section gap={24} bg="$basicwhite">
					<H2>User guides</H2>
					<Text>These documents and videos are intended to assist you with operating and maintaining your machine. Please get expert training if you have not used this machine before.</Text>
					<Box py={fileCollection.length > 0 ? 24 : 0}>
						{fileCollection &&
							fileCollection.map((file: any) => {
								let title = file.type;
								switch (file.type) {
									case "Product Info Sheet":
										title = "Product Info";
										break;
								}

								return (
									<Row key={file.url} style={{ marginBottom: 10, marginLeft: 0 }}>
										<Col>
											<Link
												href={file.url}
												component={(hovered: boolean) => {
													var styleData = {
														backgroundColor: "white",
														marginLeft: 0,
														marginRight: 0,
														paddingHorizontal: 16,
														paddingVertical: 18,
														borderRadius: 8,
														borderWidth: 1,
														borderColor: resolvedNeutral,
													};

													if (hovered) {
														styleData = { ...styleData, backgroundColor: "#E1E9F0" };
													}

													return (
														<Box style={styleData}>
															<HStack gap={12} alignItems="center">
																<FileText color={resolvedPrimarylight} />
																<Text>{title}</Text>
															</HStack>
														</Box>
													);
												}}
											/>
										</Col>
									</Row>
								);
							})}
					</Box>
					{/* <Box>
						<EquipmentVideoList />
					</Box>
					<Box my={24} gap={24}>
						<ItemDownload isFile title="User manual" url="" />
						<ItemDownload isFile title="Product info" url="" />
						<ItemDownload isFile title="Past breakdown" url="" />
					</Box> */}
				</Section>
			)}
			{/* <Box gap={16}>
				<H2>Monitoring</H2>
				<Section bg="$basicwhite">
					<TabContainer tabTitleSize="tight">
						<Tab key={"Location"} id="location" title="Location">
							<Box>
								<Text>TEST</Text>
							</Box>
						</Tab>
						<Tab key={"Battery"} id="battery" title="Battery">
							<Box>
								<Text>TEST</Text>
							</Box>
						</Tab>
						<Tab key={"Utilisation"} id="utilisation" title="Utilisation">
							<Box>
								<Text>TEST</Text>
							</Box>
						</Tab>
						<Tab key={"Uptime"} id="uptime" title="Uptime">
							<Box>
								<Text>TEST</Text>
							</Box>
						</Tab>
					</TabContainer>
					<EquipmentTracking dataList={null} />
				</Section>
			</Box> */}
			{!isPublicView && (
				<Section bg="$basicwhite">
					{/* <H2>Service history</H2> */}
					<ServiceOrderScreen equipmentId={Number(equipment.id)} isMainScreen={false} />
				</Section>
			)}
			{/* <Section gap={24} bg="$basicwhite">
				<H2>Generate QR code</H2>
				<TabContainer tabTitleSize="tight">
					<Tab key={"Download"} id="download" title="Download">
						<Box>
							<Text>TEST</Text>
						</Box>
					</Tab>
					<Tab key={"Purchase QR sticker"} id="purchaseQR" title="Purchase QR sticker">
						<Box>
							<Text>TEST</Text>
						</Box>
					</Tab>
				</TabContainer>
			</Section> */}
		</Box>
	);
}
